import { useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { useHeaderContext } from "../context/headerContext";
import DotPagination from "../components/DotPagination";
import { Link } from "react-router-dom";
import MonitorImage from "/images/onboarding-images/Monitor.png";
import AccessImage from "/images/onboarding-images/Access.png";
import EmergencyImage from "/images/onboarding-images/Emergency.png";
import ManageImage from "/images/onboarding-images/Manage.png";
import { useUserContext } from "../context/userContext";
import fetchWithRefresh from "../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../utils/LogErrors";

const cases = [
  {
    image: MonitorImage,
    title: "MONITOR",
    description1: "Easily scan participants in and out of your venue",
    description2:
      "Scan participants in and out of your venue and zones. Monitor numbers of participants and record key session data.",
  },
  {
    image: AccessImage,
    title: "ACCESS",
    description1: "Detailed information at your fingertips",
    description2:
      "Instant access to participants' ICE contacts and medical information in the app while participants are checked in.",
    bgColour: '#0C5989',
  },
  {
    image: EmergencyImage,
    title: "EMERGENCY",
    description1: "Rapid response in a medical emergency",
    description2:
      "Easily alert all staff to an emergency and access vital information.",
    link: "<a href='https://sportsguardian.b-cdn.net/SG_Venue_Staff_Guide_Feb25.pdf' target='_blank'>Read Full Staff Guide</a>",
  },
  {
    image: ManageImage,
    title: "MANAGE",
    description1: "View and manage your Venue bookings",
    description2: "Intuitive booking management for individuals and groups.",
  },
];

export default function StaffOnboarding() {
  const { setTitle } = useHeaderContext();
  const { user, logout, updateTokens } = useUserContext();

  const [currentDot, setCurrentDot] = useState(1);
  const totalDots = cases.length;

  useEffect(() => {
    setTitle("W E L C O M E");

    const toggleOnboarding = async () => {
      Sentry.startSpan({ name: "Toggle Onboarding", op: "onboarding" }, async (span) => {
        try {
          if (!user?.id) {
            Sentry.captureMessage("Onboarding toggle failed: Missing user ID", "warning");
            console.warn("Missing user ID for toggling onboarding.");
            return;
          }
    
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Users/SetStaffOnboarding`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                userLoginId: user.id,
                requireOnboarding: 0,
              }),
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );
    
          if (!response.ok) {
            const errorMsg = `Failed to toggle onboarding: ${response.status} - ${response.statusText}`;
            Sentry.captureMessage(errorMsg, "error");
            LogErrors(user, logout, updateTokens, errorMsg);
            toast.error("Error toggling onboarding.");
            return;
          }
    
          // LogErrors(user, logout, updateTokens, "Onboarding toggled successfully");
          toast.success("Onboarding toggled successfully.");
          span.setStatus("ok");
    
        } catch (error) {
          LogErrors(user, logout, updateTokens, "Network error while toggling onboarding: " + error);
          Sentry.captureException(error);
          toast.error("Network error occurred. Please try again.");
        } finally {
          span.end();
        }
      });
    };

    toggleOnboarding();
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setCurrentDot((prev) => (prev === totalDots ? 1 : prev + 1));
    },
    onSwipedRight: () => {
      setCurrentDot((prev) => (prev === 1 ? totalDots : prev - 1));
    },
  });

  const getContentByDot = (dot) => {
    return cases[dot - 1];
  };

  const getOffset = () => {
    // Ensure the offset does not go below 0
    const offset = currentDot > 0 ? (currentDot - 1) * 100 : 0;
    return -offset; // Assuming 100% width per section
  };

  const content = getContentByDot(currentDot);

  if (!content) return null;

  return (
    <div {...handlers} className="relative overflow-hidden pb-24">
      <div
        className="flex transition-transform duration-500"
        style={{
          transform: `translateX(${getOffset()}%)`,
        }}
      >
        {cases.map((content, index) => {
          // LogErrors(user, logout, updateTokens, content?.bgColour)
          return (
          <div
            key={index}
            className="flex flex-col items-center text-center flex-[0_0_100%]"
          >
            <div
              className={`flex items-center justify-center text-center mb-[60px]`}
              style={{ backgroundColor: content.bgColour || 'bg-sportsLightGrey' }}
            >
              <img
                src={content.image}
                className=" h-[333px] w-full object-cover"
                alt={content.title}
              />
            </div>
            <div className="flex flex-col items-center justify-center text-center">
              <p className="tracking-[0.2em] mb-[10px] font-semibold">
                {content.title}
              </p>
              <p className="mb-[30px] text-[24px] font-semibold px-[20px]">
                {content.description1}
              </p>
              <p className="text-center text-[#727272] px-[20px]">
                {content.description2}
              </p>
              {content.link && (
                <div
                  className="underline"
                  dangerouslySetInnerHTML={{ __html: content.link }}
                />
              )}
            </div>
          </div>
          )
        })}
      </div>
      <div className="flex flex-col gap-y-[40px] items-center mt-[44px]">
        <DotPagination
          totalDots={cases.length}
          currentDot={currentDot}
          setCurrentDot={setCurrentDot}
        />
        <Link to="/" className="get-started-link font-semibold underline">
          Get Started
        </Link>
      </div>
    </div>
  );
}
